/*
 * Luminipet — keep the submitted variant in step with APO's option choices.
 *
 * WHY THIS EXISTS
 * On the templates this script is rendered for (see lp-apo-variant-sync.liquid)
 * the theme draws no native variant picker: APO owns the product page. APO's
 * options only ever write line item properties — they never touch the variant
 * id the add-to-cart form submits. So the form posts whatever
 * `product.selected_or_first_available_variant` was when the page was rendered
 * (always the first variant), and the cart ends up with the wrong size/colour,
 * image, SKU and inventory while APO's properties describe what the customer
 * actually picked.
 *
 * WHAT IT DOES
 * Reads the selected values straight out of APO's rendered DOM and writes the
 * matching variant id into the form's `id` input.
 *
 * DELIBERATELY CONSERVATIVE — when it is not certain, it changes nothing:
 *   - APO is not on the page            -> do nothing
 *   - no APO value selected yet         -> do nothing
 *   - zero or several variants match    -> do nothing (ambiguous)
 *   - the matching variant is sold out  -> do nothing
 * A product whose native axis APO does not offer (nothing matches) is left
 * exactly as it was, never made worse.
 *
 * It writes `input.value` — a property, not an attribute — and fires no
 * events, so no MutationObserver and no APO listener is woken up. That is what
 * keeps it from looping with APO's own variant-change handling.
 *
 * LOADED ONLY WHEN: snippets/lp-apo-variant-sync.liquid decides to, which is a
 * template-suffix allowlist plus a multi-variant check.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'lp-variant-sync-config';
  var EXCLUDED = '.mw-tingle, quick-add-modal, .product-card-wrapper, .complementary-products';

  // Compare "Rose Gold" / "rose-gold" / "10 oz" / "10oz" as the same value.
  function norm(text) {
    return String(text == null ? '' : text).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function visible(element) {
    if (!element || !element.getClientRects().length) return false;
    for (var node = element; node instanceof Element; node = node.parentElement) {
      var style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  }

  // APO puts the human-readable value on the radio's aria-label; the visible
  // title div is the fallback.
  function valueName(card) {
    var input = card.querySelector('input[aria-label]');
    var label = input && input.getAttribute('aria-label');
    if (label && label.trim()) return label.trim();
    var title = card.querySelector('.mw-stack--wide__content-title');
    return title ? title.textContent.trim() : '';
  }

  // The radio's checked state is authoritative — APO's `--selected` class is a
  // styling artifact and can lag behind. Only fall back to the class when no
  // radio in the option is checked at all (APO renders non-radio value types).
  function chosenCard(option) {
    var cards = option.querySelectorAll('.mw-option__value');
    var byClass = null;
    for (var i = 0; i < cards.length; i++) {
      if (!visible(cards[i])) continue;
      if (cards[i].querySelector('input:checked')) return cards[i];
      if (!byClass && cards[i].classList.contains('mw-option__value--selected')) {
        byClass = cards[i];
      }
    }
    return byClass;
  }

  function selectedValues(scope) {
    var names = [];
    var options = scope.querySelectorAll('.mw-option');
    for (var i = 0; i < options.length; i++) {
      if (!visible(options[i])) continue;
      var card = chosenCard(options[i]);
      if (!card) continue;
      var name = valueName(card);
      if (name) names.push(norm(name));
    }
    return names;
  }

  // A variant matches when every one of its option values is among the selected
  // APO values. Order-independent, and extra APO options (photo upload, rush)
  // are simply ignored.
  function match(variants, selected) {
    var found = null;
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      var checked = 0;
      var every = true;
      for (var j = 0; j < options.length; j++) {
        var key = norm(options[j]);
        // Products with fewer than three options carry nulls in the trailing
        // slots (the Liquid emits one entry per Shopify option slot). A null is
        // an absent axis, not a value that failed to match.
        if (!key) continue;
        checked++;
        if (selected.indexOf(key) === -1) { every = false; break; }
      }
      if (!every || !checked) continue;
      if (found) return null; // ambiguous — refuse to guess
      found = variants[i];
    }
    return found;
  }

  function idInputs(section) {
    var forms = section.querySelectorAll('form[action*="/cart/add"]');
    var inputs = [];
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].closest(EXCLUDED)) continue;
      var input = forms[i].querySelector('input[name="id"]');
      if (input) inputs.push(input);
    }
    return inputs;
  }

  function init() {
    var node = document.getElementById(CONFIG_ID);
    if (!node || node.dataset.lpVariantSyncMounted === '1') return;

    var variants;
    try { variants = JSON.parse(node.textContent); } catch (_) { return; }
    if (!Array.isArray(variants) || variants.length < 2) return;

    var section = node.closest('[id^="shopify-section-"]') || document;
    var scope = section.querySelector('.product__info-container') || section;
    var inputs = idInputs(section);
    // Exactly one add-to-cart form is expected on these templates. Anything
    // else means the page is not what this script was written for.
    if (inputs.length !== 1) return;
    var input = inputs[0];
    node.dataset.lpVariantSyncMounted = '1';

    function sync() {
      var selected = selectedValues(scope);
      if (!selected.length) return null;
      var variant = match(variants, selected);
      if (!variant || !variant.available) return null;
      var id = String(variant.id);
      if (input.value !== id) {
        input.value = id;                  // property only: no event, no attribute
        if (input.disabled) input.disabled = false;
      }
      return id;
    }

    // APO's own signals, plus the radios' native change event as a fallback.
    ['mwApo::ready', 'mwApo::recalculated', 'mwApo::ON_CHANGE_OPTION'].forEach(function (name) {
      document.addEventListener(name, sync);
    });
    scope.addEventListener('change', sync);

    // Last line of defence: re-check at the moment of submit. Capture phase on
    // the section runs before the form-level handlers in lp-direct-checkout.js
    // that read FormData.
    section.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest('button[type="submit"], [name="add"], .mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .mw-group__button')) sync();
    }, true);

    window.addEventListener('pageshow', sync);
    sync();

    // Inspectable without touching the DOM (lp-product-discount.js watches the
    // info container's attributes, so nothing here may write attributes).
    window.LPVariantSync = { sync: sync, variants: variants, input: input };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
